import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MapPin, Navigation, Loader2, X, Clock } from 'lucide-react';
import { useLocation } from '@context/LocationContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import loadGoogleMaps from '@utils/loadGoogleMaps';
import {
  addLocationSearchHistory,
  matchLocationSearchHistory,
  mergeHistoryWithApiPredictions,
} from '@utils/locationSearchHistory';

const LocationPicker = ({ compact = false }) => {
  const {
    location,
    locationName,
    deviceLocationName,
    locationLoading,
    setManualLocation,
    resetToDeviceLocation,
  } = useLocation();
  const { googleMapsApiKey } = usePublicConfig();

  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState('');
  const [apiResults, setApiResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const pendingDetectHistoryRef = useRef(false);

  const googleReadyRef = useRef(false);
  const autoServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const historyMatches = useMemo(
    () => (focused && query.trim().length >= 2 ? matchLocationSearchHistory(query) : []),
    [focused, query],
  );

  const mergedResults = useMemo(
    () => mergeHistoryWithApiPredictions(historyMatches, apiResults, 5),
    [historyMatches, apiResults],
  );

  // ── Load Google Maps JS & init Places services ──────────────────────
  useEffect(() => {
    if (!googleMapsApiKey) return;
    loadGoogleMaps(googleMapsApiKey)
      .then(() => {
        googleReadyRef.current = true;
        autoServiceRef.current = new window.google.maps.places.AutocompleteService();
        const div = document.createElement('div');
        placesServiceRef.current = new window.google.maps.places.PlacesService(div);
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      })
      .catch(() => {});
  }, [googleMapsApiKey]);

  const resetSessionToken = useCallback(() => {
    if (window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  // ── Close on click outside ──────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFocused(false);
        setQuery('');
        setApiResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── After GPS detect: save history + clear detecting ───────────────
  useEffect(() => {
    if (!locationLoading && detecting) {
      if (
        pendingDetectHistoryRef.current &&
        location?.lat != null &&
        location?.lng != null &&
        locationName &&
        !['Detecting...', 'Location unavailable'].includes(locationName)
      ) {
        pendingDetectHistoryRef.current = false;
        const parts = locationName.split(',').map((s) => s.trim());
        addLocationSearchHistory({
          mainText: parts[0] || locationName,
          secondaryText: parts.slice(1).join(', '),
          lat: location.lat,
          lng: location.lng,
        });
      }
      setDetecting(false);
    }
  }, [locationLoading, detecting, location, locationName]);

  // ── Autocomplete via Places API (biased to current city) ────────────
  const doSearch = useCallback(
    (q) => {
      if (q.trim().length < 2) {
        setApiResults([]);
        return;
      }
      if (!googleReadyRef.current || !autoServiceRef.current) {
        setApiResults([]);
        return;
      }

      setSearching(true);

      const request = {
        input: q.trim(),
        sessionToken: sessionTokenRef.current,
        componentRestrictions: { country: 'in' },
      };

      if (location?.lat && location?.lng) {
        request.location = new window.google.maps.LatLng(location.lat, location.lng);
        request.radius = 50000;
      }

      autoServiceRef.current.getPlacePredictions(request, (predictions, status) => {
        setSearching(false);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setApiResults(
            predictions.map((p) => ({
              placeId: p.place_id,
              mainText: p.structured_formatting?.main_text || p.description,
              secondaryText: p.structured_formatting?.secondary_text || '',
            })),
          );
        } else {
          setApiResults([]);
        }
      });
    },
    [location],
  );

  // ── Debounce 300 ms ─────────────────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setApiResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  // ── Handlers ────────────────────────────────────────────────────────
  const handleFocus = () => {
    setFocused(true);
    setQuery('');
  };

  const handleSelect = (result) => {
    if (result.isHistory && result.lat != null && result.lng != null) {
      const label = [result.mainText, result.secondaryText].filter(Boolean).join(', ');
      setManualLocation(result.lat, result.lng, label);
      addLocationSearchHistory({
        mainText: result.mainText,
        secondaryText: result.secondaryText,
        lat: result.lat,
        lng: result.lng,
        placeId: result.placeId,
      });
      setFocused(false);
      setQuery('');
      setApiResults([]);
      return;
    }

    if (result.placeId && placesServiceRef.current) {
      placesServiceRef.current.getDetails(
        { placeId: result.placeId, fields: ['geometry'] },
        (place, status) => {
          if (status === 'OK' && place?.geometry?.location) {
            const parts = [result.mainText];
            if (result.secondaryText) {
              parts.push(
                result.secondaryText.split(',').slice(0, 2).join(',').trim(),
              );
            }
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setManualLocation(lat, lng, parts.join(', '));
            addLocationSearchHistory({
              mainText: result.mainText,
              secondaryText: result.secondaryText,
              lat,
              lng,
              placeId: result.placeId,
            });
          }
          resetSessionToken();
        },
      );
    }
    setFocused(false);
    setQuery('');
    setApiResults([]);
  };

  const handleDetectLocation = () => {
    pendingDetectHistoryRef.current = true;
    setDetecting(true);
    resetToDeviceLocation();
    setFocused(false);
    setQuery('');
    setApiResults([]);
    resetSessionToken();
  };

  const showDropdown = focused;

  return (
    <div ref={containerRef} className="relative w-full">
      {/* ── Input bar ─────────────────────────────────────────────── */}
      <div className="relative">
        <MapPin
          size={15}
          strokeWidth={2}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500 dark:text-surface-400 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={focused ? query : ''}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          placeholder={
            focused
              ? 'Type a place, street, or city...'
              : locationLoading
                ? 'Detecting...'
                : locationName
          }
          className={`w-full rounded-xl border bg-white py-2.5 pl-9 pr-9 text-sm transition-all duration-200 hover:bg-surface-50 active:scale-[0.997] focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:bg-surface-800/60 dark:hover:bg-surface-800/80 dark:text-white dark:placeholder:text-surface-400 ${
            focused
              ? 'border-brand-400 dark:border-brand-400 text-surface-900'
              : 'border-surface-200/80 dark:border-surface-700/50 text-surface-800 placeholder:text-surface-600 dark:placeholder:text-surface-300'
          }`}
        />

        {focused ? (
          <button
            type="button"
            onClick={() => {
              setFocused(false);
              setQuery('');
              setApiResults([]);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-lg text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
          >
            <X size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleDetectLocation}
            disabled={detecting || locationLoading}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-lg text-surface-400 hover:bg-surface-100 hover:text-brand-500 dark:hover:bg-surface-700 dark:hover:text-brand-400 transition-colors disabled:opacity-50"
            title="Use my current location"
          >
            {detecting || locationLoading ? (
              <Loader2 size={14} className="animate-spin text-brand-500" />
            ) : (
              <Navigation size={14} />
            )}
          </button>
        )}
      </div>

      {/* ── Dropdown ──────────────────────────────────────────────── */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl border border-surface-200/80 bg-white shadow-xl dark:border-surface-700 dark:bg-surface-900 overflow-hidden">
          {/* GPS option */}
          <button
            type="button"
            onClick={handleDetectLocation}
            className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800/80 border-b border-surface-100 dark:border-surface-800 transition-colors"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 dark:bg-brand-500/10">
              <Navigation size={14} className="text-brand-500" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-surface-800 dark:text-white">
                Use my current location
              </p>
              <p className="text-[11px] text-surface-400 truncate">
                {deviceLocationName}
              </p>
            </div>
          </button>

          {/* Loading (API in flight) */}
          {searching && query.trim().length >= 2 && (
            <div className="flex items-center justify-center gap-2 py-3 text-xs text-surface-400 border-t border-surface-100 dark:border-surface-800">
              <Loader2 size={14} className="animate-spin" />
              Finding places...
            </div>
          )}

          {/* No results */}
          {!searching &&
            mergedResults.length === 0 &&
            query.trim().length >= 2 && (
              <div className="py-4 text-center text-xs text-surface-400">
                No places found for &ldquo;{query}&rdquo;
              </div>
            )}

          {/* Results (history first, then API — max 5) */}
          {mergedResults.length > 0 && (
            <div className="max-h-52 overflow-y-auto divide-y divide-surface-100 dark:divide-surface-800">
              {mergedResults.map((r) => (
                <button
                  key={r.listKey}
                  type="button"
                  onClick={() => handleSelect(r)}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-50 dark:hover:bg-surface-800/80 transition-colors"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-100 dark:bg-surface-800">
                    {r.isHistory ? (
                      <Clock size={15} strokeWidth={2} className="text-surface-500 dark:text-surface-400" />
                    ) : (
                      <MapPin size={15} strokeWidth={2} className="text-surface-500 dark:text-surface-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-surface-800 dark:text-white truncate">
                      {r.mainText}
                    </p>
                    {r.secondaryText ? (
                      <p className="text-[11px] text-surface-400 truncate">
                        {r.secondaryText}
                      </p>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Hint */}
          {query.trim().length < 2 && (
            <div className="py-3 px-3 text-[11px] text-surface-400">
              Start typing a place name to see suggestions
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
